import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, ButtonGroup, Dropdown, Spinner } from 'react-bootstrap';
import { ExternalLink, History, Link2, Monitor, RotateCw, Smartphone, Tablet } from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { installCanvasBridge } from './canvasBridge';
import VersionFrame from './VersionFrame';

const VIEWPORTS = [
  { key: 'desktop', label: 'Desktop', width: null, Icon: Monitor },
  { key: 'tablet', label: 'Tablet (820px)', width: 820, Icon: Tablet },
  { key: 'mobile', label: 'Mobile (390px)', width: 390, Icon: Smartphone },
];

/**
 * Frame style for a device width. When the stage is narrower than the device (a phone or tablet
 * admin screen), the frame keeps the device width so the page lays out as it would there, and is
 * scaled down to fit; negative margins give back the space the transform does not.
 */
export const frameStyle = (width, stage) => {
  if (!width) return undefined;
  const scale = stage && stage.width < width ? stage.width / width : 1;
  // A sliver of overflow is not worth blurring the preview for
  if (scale >= 0.98) return { width, flex: '0 0 auto' };
  const height = stage.height / scale;
  return {
    width,
    height,
    flex: '0 0 auto',
    transform: `scale(${scale})`,
    transformOrigin: '0 0',
    marginRight: width * (scale - 1),
    marginBottom: height * (scale - 1),
  };
};

/** Content-box size of the stage, kept current as panes and the window resize. */
const useStageSize = (ref) => {
  const [size, setSize] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const cs = getComputedStyle(el);
      const width = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const height = el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      setSize((prev) => (prev && prev.width === width && prev.height === height ? prev : { width, height }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
};

/**
 * The page preview with click-to-edit. The iframe must keep exactly this sandbox: allow-scripts is
 * required for WebKit to deliver the bridge's listeners, and page scripts are blocked by the CSP
 * meta the server injects as the first child of <head>. Never add allow-forms, allow-popups or
 * allow-top-navigation.
 */
const EditorCanvas = ({
  page,
  loading,
  canEdit,
  actions,
  allowNavigate = true,
  historyOpen = false,
  version = null,
  prodState = null,
  onHistory,
  onOpenHistory,
  onBridge,
  onSelect,
  onChange,
  onNavigate,
  onSaveShortcut,
  onReload,
}) => {
  const [viewport, setViewport] = useState('desktop');
  // Phones put the test site, production site and reload links in one menu
  const isMobile = useIsMobile();
  const [chip, setChip] = useState(null);
  const frameRef = useRef(null);
  const stageRef = useRef(null);
  const bridgeRef = useRef(null);

  // The bridge is installed once per loaded document; callbacks go through a ref so it always
  // calls the latest handlers without reinstalling.
  const handlers = useRef({});
  handlers.current = { onSelect, onChange, onNavigate, onSaveShortcut };
  // Read lazily by the bridge: permissions arrive after the first render, and reinstalling would
  // mean reassigning srcdoc, which WebKit ignores when the value is unchanged (no load event).
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;

  const teardown = useCallback(() => {
    bridgeRef.current?.destroy();
    bridgeRef.current = null;
    frameRef.current?.removeAttribute('data-bridge');
    setChip(null);
    onBridge(null);
  }, [onBridge]);

  useEffect(() => teardown, [teardown]);

  // A native load listener attached before srcdoc is assigned: React's onLoad on a srcDoc iframe is
  // not reliable in WebKit (the event can fire for the initial blank document or before React
  // listens), which left the bridge uninstalled. This is the order the Phase 0a spike verified.
  // A fresh iframe for every fetched page object, including a reload of identical content
  // (Discard, after a save): reassigning an unchanged srcdoc does not reload in WebKit.
  const loads = useRef(0);
  const frameKey = useMemo(() => (page ? ++loads.current : 0), [page]);
  const previewHtml = page?.previewHtml;
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || previewHtml == null) return undefined;
    const install = () => {
      teardown();
      const doc = frame.contentDocument;
      if (!doc || !doc.querySelector('[data-tt-unit], [data-tt-chrome]')) {
        return; // the initial about:blank, or a page with nothing editable
      }
      const bridge = installCanvasBridge(doc, {
        canEdit: () => canEditRef.current,
        onSelect: (id) => handlers.current.onSelect(id),
        onChange: (id, value) => handlers.current.onChange(id, value),
        onNavigate: (href) => handlers.current.onNavigate(href),
        onSaveShortcut: () => handlers.current.onSaveShortcut(),
        onLinkChip: (info) => setChip(info),
      });
      bridgeRef.current = bridge;
      frame.setAttribute('data-bridge', 'ready');
      onBridge(bridge);
    };
    frame.addEventListener('load', install);
    frame.srcdoc = previewHtml;
    return () => {
      frame.removeEventListener('load', install);
      teardown();
    };
  }, [frameKey, previewHtml, teardown, onBridge]);

  const width = VIEWPORTS.find((v) => v.key === viewport)?.width;
  const stageSize = useStageSize(stageRef);
  const fitted = frameStyle(width, stageSize);
  const scale = fitted?.transform ? stageSize.width / width : 1;

  const chipStyle = () => {
    const frame = frameRef.current;
    const stage = stageRef.current;
    if (!chip || !frame || !stage) return { display: 'none' };
    const f = frame.getBoundingClientRect();
    const s = stage.getBoundingClientRect();
    const above = chip.rect.top > 40;
    // The chip's rect is in the frame's own pixels, which are scaled when the frame is fitted
    const top = f.top - s.top + stage.scrollTop + (above ? chip.rect.top * scale - 36 : chip.rect.bottom * scale + 6);
    const left = f.left - s.left + stage.scrollLeft + Math.max(0, chip.rect.left * scale);
    return { top, left };
  };

  return (
    <section className="mse-canvas">
      <div className="mse-toolbar">
        <code className="mse-url" title={page?.path}>
          {page?.url || ' '}
        </code>
        {prodState && (
          <button
            type="button"
            className={`badge border-0 ${prodState === 'pending' ? 'bg-warning' : 'bg-success'}`}
            onClick={onOpenHistory}
            title="The preview shows the test site. Open this page's history."
          >
            {prodState === 'pending' ? 'Not yet in production' : 'Same as production'}
          </button>
        )}
        <ButtonGroup size="sm" aria-label="Preview width">
          {VIEWPORTS.map(({ key, label, Icon }) => (
            <Button
              key={key}
              variant="outline-secondary"
              active={viewport === key}
              onClick={() => setViewport(key)}
              title={label}
              aria-label={label}
            >
              <Icon size={14} />
            </Button>
          ))}
        </ButtonGroup>
        <div className="mse-toolbar-spacer" />
        {page && !isMobile && (
          <>
            <Button size="sm" variant="outline-secondary" href={page.testUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} className="me-1" />
              Test site
            </Button>
            <Button size="sm" variant="outline-secondary" href={page.prodUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} className="me-1" />
              Production site
            </Button>
          </>
        )}
        {onHistory && page && !page.shared && (
          <Button
            size="sm"
            variant="outline-secondary"
            active={historyOpen}
            onClick={onHistory}
            title="Page history"
            aria-label="Page history"
          >
            <History size={14} />
          </Button>
        )}
        {isMobile ? (
          <Dropdown align="end">
            <Dropdown.Toggle size="sm" variant="outline-secondary" id="mse-canvas-more">
              More
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item href={page?.testUrl} target="_blank" rel="noopener noreferrer" disabled={!page}>
                <ExternalLink size={14} className="me-2" />
                Open on test site
              </Dropdown.Item>
              <Dropdown.Item href={page?.prodUrl} target="_blank" rel="noopener noreferrer" disabled={!page}>
                <ExternalLink size={14} className="me-2" />
                Open production site
              </Dropdown.Item>
              <Dropdown.Item as="button" onClick={onReload} disabled={!page || loading}>
                <RotateCw size={14} className="me-2" />
                Reload from GitHub
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ) : (
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={onReload}
            disabled={!page || loading}
            title="Reload from GitHub"
            aria-label="Reload from GitHub"
          >
            <RotateCw size={14} />
          </Button>
        )}
        {actions}
      </div>
      {version?.banner}
      <div className="mse-stage" ref={stageRef}>
        {version?.html && (
          <div className="mse-version-layer">
            <VersionFrame html={version.html} style={fitted} title="Read-only preview of a past version" />
          </div>
        )}
        {loading && (
          <div className="mse-stage-overlay">
            <Spinner animation="border" size="sm" /> Loading page
          </div>
        )}
        {page && (
          <iframe
            key={frameKey}
            ref={frameRef}
            title={`Editable preview of ${page.url}`}
            className="mse-frame"
            style={fitted}
            sandbox="allow-same-origin allow-scripts"
          />
        )}
        {chip && (
          <div className="mse-link-chip" style={chipStyle()}>
            <Link2 size={14} />
            <span className="mse-link-chip-href" title={chip.href}>
              {chip.href}
            </span>
            {allowNavigate && (
              <button type="button" onClick={() => onNavigate(chip.href)}>
                Follow
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default EditorCanvas;
